import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Group,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconPlus,
  IconRosetteDiscount,
} from "@tabler/icons-react";
import { useDiscountPresets } from "../../../context/DiscountPresetsContext";
import { useResponsive } from "../../../context/ResponsiveContext";
import usePermissions from "../../../hooks/usePermissions";
import { DiscountPreset } from "../../../types/discounts";
import { formatDiscountValue } from "../../../utils/discountFormatting";
import { notifyError, notifySuccess } from "../../../utils/notify";
import { StandardTableShell } from "../../common/StandardTable";
import EmptyStateCard from "../../common/surface/EmptyStateCard";
import DeleteConfirmationModal from "../../common/DeleteConfirmationModal";
import {
  SettingsListSkeleton,
  SettingsPageHeader,
  SettingsPageLayout,
} from "../common";
import DiscountPresetActions from "./DiscountPresetActions";
import DiscountPresetCard from "./DiscountPresetCard";
import DiscountPresetFormModal from "./DiscountPresetFormModal";
import QuickDiscountsSettings from "./QuickDiscountsSettings";
import { DISCOUNT_TABLE_COLUMN_WIDTHS as COLUMN_WIDTHS } from "./discountTableColumns";

const deleteMessage = (preset: DiscountPreset): string =>
  `Are you sure you want to delete the "${preset.name}" discount preset? ` +
  "If it's been used in past transactions, you'll still see it in your reports.";

/**
 * Settings → Offerings → Discounts: the location's discount presets. Staff
 * pick these when applying a discount to a ticket or Quick Sale.
 *
 * A "Quick discounts" card (up to three, reorderable — see
 * QuickDiscountsSettings) sits above the full list once presets exist.
 *
 * Table on desktop/tablet, stacked cards on phones. Reads and mutates the
 * shared DiscountPresetsContext, so changes show up in any open
 * apply-discount modal immediately. Add/Edit need
 * `settingsDiscounts.canManagePresets`; Delete needs `canDeletePresets`.
 */
export default function DiscountPresetsSettings() {
  const { presets, loading, error, refresh, removePreset } =
    useDiscountPresets();
  const { isMobile } = useResponsive();
  const perms = usePermissions();
  const canManage = perms.can("settingsDiscounts", "canManagePresets");
  const canDelete = perms.can("settingsDiscounts", "canDeletePresets");
  const hasActions = canManage || canDelete;

  const [formOpened, setFormOpened] = useState(false);
  const [editingPreset, setEditingPreset] = useState<DiscountPreset | null>(
    null,
  );
  const [deletingPreset, setDeletingPreset] = useState<DiscountPreset | null>(
    null,
  );
  const [isDeleting, setIsDeleting] = useState(false);

  const openAdd = () => {
    setEditingPreset(null);
    setFormOpened(true);
  };

  const openEdit = (preset: DiscountPreset) => {
    setEditingPreset(preset);
    setFormOpened(true);
  };

  const closeForm = () => {
    setFormOpened(false);
    setEditingPreset(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingPreset) return;
    const { id, name } = deletingPreset;
    setIsDeleting(true);
    try {
      await removePreset(id);
      notifySuccess(`${name} was deleted.`, {
        title: "Discount preset deleted",
      });
      setDeletingPreset(null);
    } catch (err) {
      console.error("Error deleting discount preset:", err);
      notifyError(
        (err as Error | null)?.message ||
          "Failed to delete discount. Please try again.",
        { title: "Delete failed" },
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const renderBody = () => {
    if (loading && presets.length === 0) {
      return (
        <SettingsListSkeleton
          rows={3}
          ariaLabel="Loading discounts"
          data-testid="discount-presets-loading"
        />
      );
    }

    if (error && presets.length === 0) {
      return (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Couldn't load discounts"
          color="var(--color-danger)"
        >
          <Group justify="space-between" align="center">
            <Text size="sm">
              Something went wrong loading your discounts. Please try again.
            </Text>
            <Button
              size="xs"
              variant="light"
              color="var(--color-danger)"
              onClick={() => refresh()}
            >
              Retry
            </Button>
          </Group>
        </Alert>
      );
    }

    if (presets.length === 0) {
      return (
        <EmptyStateCard
          icon={<IconRosetteDiscount size={20} stroke={1.9} />}
          title="No discounts yet"
          message="Create discounts your team can apply to tickets and Quick Sales."
          actionLabel={canManage ? "Add Discount" : undefined}
          onAction={canManage ? openAdd : undefined}
        />
      );
    }

    return (
      <Stack gap="lg">
        <QuickDiscountsSettings canManage={canManage} />
        <Stack gap="sm">
          <Title order={5} fw={600}>
            All discounts
          </Title>
          {renderPresetList()}
        </Stack>
      </Stack>
    );
  };

  const renderPresetList = () => {
    if (isMobile) {
      return (
        <Stack gap="sm">
          {presets.map((preset) => (
            <DiscountPresetCard
              key={preset.id}
              preset={preset}
              canEdit={canManage}
              canDelete={canDelete}
              onEdit={openEdit}
              onDelete={setDeletingPreset}
            />
          ))}
        </Stack>
      );
    }

    return (
      <StandardTableShell minWidth={480} ariaLabel="Discount presets">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Name</Table.Th>
            <Table.Th w={COLUMN_WIDTHS.type}>Type</Table.Th>
            <Table.Th w={COLUMN_WIDTHS.amount}>Amount</Table.Th>
            {hasActions && (
              <Table.Th w={COLUMN_WIDTHS.actions}>Actions</Table.Th>
            )}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {presets.map((preset) => (
            <Table.Tr key={preset.id}>
              <Table.Td>
                <Text size="sm" fw={500}>
                  {preset.name}
                </Text>
              </Table.Td>
              <Table.Td>
                <Text size="sm" c="dimmed">
                  {preset.discount_type}
                </Text>
              </Table.Td>
              <Table.Td>
                <Text size="sm">
                  {formatDiscountValue(preset.discount_type, preset.amount)}
                </Text>
              </Table.Td>
              {hasActions && (
                <Table.Td>
                  <DiscountPresetActions
                    preset={preset}
                    canEdit={canManage}
                    canDelete={canDelete}
                    onEdit={openEdit}
                    onDelete={setDeletingPreset}
                  />
                </Table.Td>
              )}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </StandardTableShell>
    );
  };

  return (
    <SettingsPageLayout
      header={
        <SettingsPageHeader
          title="Discounts"
          subtitle="Reusable discounts your team can apply to tickets and Quick Sales. Several can stack on one item."
          actions={
            canManage ? (
              <Button
                color="var(--brand-teal)"
                leftSection={<IconPlus size={16} />}
                onClick={openAdd}
              >
                Add Discount
              </Button>
            ) : undefined
          }
        />
      }
    >
      <Box>{renderBody()}</Box>

      {canManage && (
        <DiscountPresetFormModal
          opened={formOpened}
          onClose={closeForm}
          preset={editingPreset}
        />
      )}
      {canDelete && (
        <DeleteConfirmationModal
          isOpen={deletingPreset !== null}
          onClose={() => setDeletingPreset(null)}
          onConfirm={handleDeleteConfirm}
          title="Delete Discount Preset"
          message={deletingPreset ? deleteMessage(deletingPreset) : ""}
          isLoading={isDeleting}
        />
      )}
    </SettingsPageLayout>
  );
}
